import type { Fund, Tenant, UnitClass } from '../types';

export const FIDES_TENANT_ID = 't-fides';
export const HARBOUR_TENANT_ID = 't-harbour';
export const MIGRATION_DATE = '2026-06-30';
export const REGISTRY_LIVE_DATE = '2026-07-01';

export const TENANTS: Tenant[] = [
  {
    id: FIDES_TENANT_ID,
    name: 'Fides Capital',
    shortName: 'Fides',
    abn: '00 000 000 001',
    afsl: 'Corporate Authorised Representative (demo)',
    accent: '#143c34',
    supportEmail: 'registry@fides.example',
  },
  {
    id: HARBOUR_TENANT_ID,
    name: 'Harbourline Partners (demo tenant)',
    shortName: 'Harbourline',
    abn: '00 000 000 002',
    afsl: 'AFSL 000000 (demo)',
    accent: '#1f4e79',
    supportEmail: 'registry@harbourline.example',
  },
];

const fundBank = (name: string, suffix: string) => ({
  bsb: '083-004',
  accountNumber: `5512${suffix}`,
  accountName: `${name} Applications Account`,
  verified: true,
});

export const FUNDS: Fund[] = [
  {
    id: 'f-psd',
    tenantId: FIDES_TENANT_ID,
    name: 'Fides Private Senior Debt Fund',
    code: 'PSDF',
    strategy: 'Private Credit',
    status: 'OPEN',
    basis: 'FIXED_UNITS',
    trustee: 'Demo Trustee Services Pty Ltd',
    abn: '00 111 111 111',
    inceptionDate: '2023-02-01',
    distributionFrequency: 'MONTHLY',
    targetSize: 60_000_000,
    bank: fundBank('PSDF', '0101'),
    apcaUserId: '301500',
    bpayBillerCode: '384521',
    registryLiveDate: REGISTRY_LIVE_DATE,
    migratedFrom: 'Registry Direct',
    description:
      'Open-ended pooled fund lending first-mortgage senior debt to residential and land subdivision projects. $1.00 fixed units, monthly distributions.',
  },
  {
    id: 'f-opp',
    tenantId: FIDES_TENANT_ID,
    name: 'Fides Opportunity Fund I',
    code: 'OPP1',
    strategy: 'Opportunistic',
    status: 'CLOSED',
    basis: 'COMMITMENT',
    trustee: 'Demo Trustee Services Pty Ltd',
    abn: '00 222 222 222',
    inceptionDate: '2025-02-15',
    maturityDate: '2030-02-15',
    distributionFrequency: 'QUARTERLY',
    targetSize: 40_000_000,
    bank: fundBank('OPP1', '0202'),
    apcaUserId: '301501',
    bpayBillerCode: '384522',
    registryLiveDate: REGISTRY_LIVE_DATE,
    migratedFrom: 'Registry Direct',
    description:
      'Closed-ended commitment fund taking preferred equity and JV positions. Capital drawn by calls over a three-year investment period.',
  },
  {
    id: 'f-mez',
    tenantId: FIDES_TENANT_ID,
    name: 'Fides Mezzanine Trust Series 2',
    code: 'MEZ2',
    strategy: 'Private Credit',
    status: 'CLOSED',
    basis: 'FIXED_UNITS',
    trustee: 'Demo Trustee Services Pty Ltd',
    abn: '00 333 333 333',
    inceptionDate: '2024-07-01',
    maturityDate: '2027-12-31',
    distributionFrequency: 'QUARTERLY',
    targetSize: 25_000_000,
    bank: fundBank('MEZ2', '0303'),
    apcaUserId: '301502',
    bpayBillerCode: '384523',
    registryLiveDate: REGISTRY_LIVE_DATE,
    migratedFrom: 'Registry Direct',
    description:
      'Single-project mezzanine facility, fixed term to December 2027. $1.00 units, quarterly interest distributions.',
  },
  {
    id: 'f-core',
    tenantId: FIDES_TENANT_ID,
    name: 'Fides Core Income Trust',
    code: 'CORE',
    strategy: 'Core',
    status: 'OPEN',
    basis: 'NAV_UNITS',
    trustee: 'Demo Trustee Services Pty Ltd',
    abn: '00 444 444 444',
    inceptionDate: '2023-07-01',
    distributionFrequency: 'QUARTERLY',
    targetSize: 80_000_000,
    bank: fundBank('CORE', '0404'),
    apcaUserId: '301503',
    bpayBillerCode: '384524',
    registryLiveDate: REGISTRY_LIVE_DATE,
    migratedFrom: 'Registry Direct',
    description:
      'Open-ended income trust holding stabilised, income-producing property. Units priced monthly at NAV, with a distribution reinvestment plan.',
  },
  {
    id: 'f-sdf2',
    tenantId: FIDES_TENANT_ID,
    name: 'Fides Senior Debt Fund II',
    code: 'SDF2',
    strategy: 'Private Credit',
    status: 'RAISING',
    basis: 'FIXED_UNITS',
    trustee: 'Demo Trustee Services Pty Ltd',
    abn: '00 555 555 555',
    inceptionDate: '2026-10-01',
    distributionFrequency: 'MONTHLY',
    targetSize: 50_000_000,
    bank: fundBank('SDF2', '0505'),
    apcaUserId: '301504',
    bpayBillerCode: '384525',
    registryLiveDate: '2026-10-01',
    description:
      'New senior debt fund, first registered natively on the Fides platform. First close 1 October 2026; applications open.',
  },
  {
    id: 'f-brg1',
    tenantId: FIDES_TENANT_ID,
    name: 'Fides Bridging Trust 1 (Eastfield)',
    code: 'BRG1',
    strategy: 'Private Credit',
    status: 'WOUND_UP',
    basis: 'FIXED_UNITS',
    trustee: 'Demo Trustee Services Pty Ltd',
    abn: '00 777 777 771',
    inceptionDate: '2025-05-01',
    maturityDate: '2026-11-30',
    distributionFrequency: 'QUARTERLY',
    targetSize: 6_000_000,
    bank: fundBank('BRG1', '0707'),
    apcaUserId: '301505',
    bpayBillerCode: '384526',
    registryLiveDate: REGISTRY_LIVE_DATE,
    migratedFrom: 'Registry Direct',
    description:
      'Single-loan bridging trust. The borrower repaid early on 15 August 2026; capital was returned and the trust wound up on 20 August 2026.',
  },
  {
    id: 'f-brg2',
    tenantId: FIDES_TENANT_ID,
    name: 'Fides Bridging Trust 2 (Riverside)',
    code: 'BRG2',
    strategy: 'Private Credit',
    status: 'CLOSED',
    basis: 'FIXED_UNITS',
    trustee: 'Demo Trustee Services Pty Ltd',
    abn: '00 777 777 772',
    inceptionDate: '2025-09-01',
    maturityDate: '2027-01-31',
    distributionFrequency: 'QUARTERLY',
    targetSize: 8_000_000,
    bank: fundBank('BRG2', '0808'),
    apcaUserId: '301506',
    bpayBillerCode: '384527',
    registryLiveDate: REGISTRY_LIVE_DATE,
    migratedFrom: 'Registry Direct',
    description:
      'Single-loan bridging trust. The borrower repaid in full on 14 October 2026, three months early. Ready to return capital and wind up.',
  },
  {
    id: 'f-hdcf',
    tenantId: HARBOUR_TENANT_ID,
    name: 'Harbourline Diversified Credit Fund',
    code: 'HDCF',
    strategy: 'Private Credit',
    status: 'OPEN',
    basis: 'FIXED_UNITS',
    trustee: 'Harbour Trustee Co (demo)',
    abn: '00 666 666 666',
    inceptionDate: '2022-03-01',
    distributionFrequency: 'MONTHLY',
    targetSize: 30_000_000,
    bank: fundBank('HDCF', '0606'),
    apcaUserId: '301600',
    bpayBillerCode: '384600',
    migratedFrom: 'Automic',
    description:
      'Demo second tenant, used to show that each manager only ever sees its own register.',
  },
];

const flatHistory = (price: number) => [{ date: '2023-01-01', price }];

const coreHistory = (() => {
  const points: { date: string; price: number }[] = [];
  let price = 1;
  for (let index = 0; index < 40; index += 1) {
    const year = 2023 + Math.floor((index + 6) / 12);
    const month = ((index + 6) % 12) + 1;
    price = Math.round((price + 0.0011 + Math.sin(index / 3) * 0.0016) * 10_000) / 10_000;
    points.push({ date: `${year}-${String(month).padStart(2, '0')}-01`, price });
  }
  return points;
})();

export const CLASSES: UnitClass[] = [
  {
    id: 'c-psd-a',
    fundId: 'f-psd',
    name: 'Class A',
    code: 'A',
    issuePrice: 1,
    unitPrice: 1,
    targetReturnPct: 8.5,
    managementFeePct: 1.25,
    minInvestment: 100_000,
    open: true,
    priceHistory: flatHistory(1),
  },
  {
    id: 'c-psd-w',
    fundId: 'f-psd',
    name: 'Class W (Wholesale $1m+)',
    code: 'W',
    issuePrice: 1,
    unitPrice: 1,
    targetReturnPct: 9,
    managementFeePct: 0.9,
    minInvestment: 1_000_000,
    open: true,
    priceHistory: flatHistory(1),
  },
  {
    id: 'c-opp-o',
    fundId: 'f-opp',
    name: 'Ordinary Units',
    code: 'ORD',
    issuePrice: 1,
    unitPrice: 1,
    targetReturnPct: 16,
    managementFeePct: 1.5,
    minInvestment: 250_000,
    open: false,
    priceHistory: flatHistory(1),
  },
  {
    id: 'c-mez-o',
    fundId: 'f-mez',
    name: 'Ordinary Units',
    code: 'ORD',
    issuePrice: 1,
    unitPrice: 1,
    targetReturnPct: 12,
    managementFeePct: 1,
    minInvestment: 100_000,
    open: false,
    priceHistory: flatHistory(1),
  },
  {
    id: 'c-core-a',
    fundId: 'f-core',
    name: 'Class A',
    code: 'A',
    issuePrice: 1,
    unitPrice: coreHistory[coreHistory.length - 1].price,
    targetReturnPct: 7,
    managementFeePct: 0.85,
    minInvestment: 50_000,
    open: true,
    priceHistory: coreHistory,
  },
  {
    id: 'c-sdf2-a',
    fundId: 'f-sdf2',
    name: 'Class A',
    code: 'A',
    issuePrice: 1,
    unitPrice: 1,
    targetReturnPct: 9,
    managementFeePct: 1.2,
    minInvestment: 100_000,
    open: true,
    priceHistory: flatHistory(1),
  },
  {
    id: 'c-brg1-o',
    fundId: 'f-brg1',
    name: 'Ordinary Units',
    code: 'ORD',
    issuePrice: 1,
    unitPrice: 1,
    targetReturnPct: 11,
    managementFeePct: 1,
    minInvestment: 100_000,
    open: false,
    priceHistory: flatHistory(1),
  },
  {
    id: 'c-brg2-o',
    fundId: 'f-brg2',
    name: 'Ordinary Units',
    code: 'ORD',
    issuePrice: 1,
    unitPrice: 1,
    targetReturnPct: 10.5,
    managementFeePct: 1,
    minInvestment: 100_000,
    open: false,
    priceHistory: flatHistory(1),
  },
  {
    id: 'c-hdcf-a',
    fundId: 'f-hdcf',
    name: 'Ordinary Units',
    code: 'ORD',
    issuePrice: 1,
    unitPrice: 1,
    targetReturnPct: 8,
    managementFeePct: 1.1,
    minInvestment: 50_000,
    open: true,
    priceHistory: flatHistory(1),
  },
];

export function priceAt(unitClass: UnitClass, isoDate: string): number {
  let price = unitClass.priceHistory[0]?.price ?? unitClass.unitPrice;
  for (const point of unitClass.priceHistory) {
    if (point.date <= isoDate) price = point.price;
  }
  return price;
}
